import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type {
  ResourceAppointmentProfile,
  ResourceAppointmentProfileInput,
} from '@/types/resource-schedule-api';
import { SCHEDULE_OWN_ERROR_HANDLING } from './use-resource-calendar-settings';
import {
  resourceScheduleQueryKey,
  resourceScheduleUrl,
  toResourceScheduleError,
} from './use-resource-schedule';

function profilesUrl(resourceId: string, id?: string): string {
  const base = `${resourceScheduleUrl(resourceId)}/AppointmentProfiles`;
  return id ? `${base}/${encodeURIComponent(id)}` : base;
}

/**
 * Add an appointment profile to a device — PROD-2007.
 *
 *   POST /api/Resource/{resourceId}/Schedule/AppointmentProfiles  -> 201
 *   body: { profileName, minutesDuration (1..1440), color (<= 20 chars),
 *           appointmentTypeId (<= 50, dbo.AppointmentTypes code, optional),
 *           isDefault, inAppBooking }
 *
 * The name is unique among the device's LIVE profiles (case-insensitive).
 * Saving `isDefault: true` clears the previous default server-side, which is
 * why success invalidates the whole aggregate rather than patching one row.
 */
export function useCreateResourceAppointmentProfile(resourceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResourceAppointmentProfileInput) => {
      try {
        return await post<ResourceAppointmentProfile>(profilesUrl(resourceId), input);
      } catch (err) {
        throw toResourceScheduleError(err);
      }
    },
    meta: SCHEDULE_OWN_ERROR_HANDLING,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceScheduleQueryKey(resourceId) });
    },
  });
}

export interface UpdateResourceAppointmentProfileInput {
  id: string;
  input: ResourceAppointmentProfileInput;
}

/**
 *   PUT /api/Resource/{resourceId}/Schedule/AppointmentProfiles/{id}  -> 200
 */
export function useUpdateResourceAppointmentProfile(resourceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: UpdateResourceAppointmentProfileInput) => {
      try {
        return await put<ResourceAppointmentProfile>(profilesUrl(resourceId, id), input);
      } catch (err) {
        throw toResourceScheduleError(err);
      }
    },
    meta: SCHEDULE_OWN_ERROR_HANDLING,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceScheduleQueryKey(resourceId) });
    },
  });
}

/**
 * Archive one appointment profile. There is no delete and no restore — a
 * needed profile is re-created.
 *
 *   POST /api/Resource/{resourceId}/Schedule/AppointmentProfiles/{id}/Archive  -> 200
 *
 * No body: `post(url, undefined)` serialises to no request body at all.
 */
export function useArchiveResourceAppointmentProfile(resourceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        return await post<ResourceAppointmentProfile>(
          `${profilesUrl(resourceId, id)}/Archive`,
          undefined,
        );
      } catch (err) {
        throw toResourceScheduleError(err);
      }
    },
    meta: SCHEDULE_OWN_ERROR_HANDLING,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resourceScheduleQueryKey(resourceId) });
    },
  });
}
